import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Button, CircularProgress, Chip,
  Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import PlaceIcon from '@mui/icons-material/Place';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import WmsBarcodeScanner from '../../shared/WmsBarcodeScanner';
import WmsNotification, { useWmsNotification } from '../../shared/WmsNotification';
import ReceivingNavMap from './ReceivingNavMap';
import receivingService, { ReceivingItemDto, PutawaySuggestion } from '../../services/receivingService';
import { useTranslation } from 'react-i18next';
import { normalizeScanValue, matchesScannedLocation } from '../../shared/scanMatching';

interface ReceivingPutawayProps {
  sessionId: string;
  warehouseId: string;
  item?: ReceivingItemDto;
  bulkItems?: ReceivingItemDto[];
  onDone: (updatedItems: ReceivingItemDto[]) => void;
  onBack: () => void;
}

const ReceivingPutaway: React.FC<ReceivingPutawayProps> = ({
  sessionId, warehouseId, item, bulkItems, onDone, onBack,
}) => {
  const { t } = useTranslation('warehouse');
  const { notification, notify, clear } = useWmsNotification();
  const isBulk = !!bulkItems && bulkItems.length > 0;
  const [suggestions, setSuggestions] = useState<Map<string, PutawaySuggestion>>(new Map());
  const [loading, setLoading] = useState(true);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [completed, setCompleted] = useState<string[]>([]);

  const allItems = isBulk ? bulkItems! : item ? [item] : [];
  const currentItem = allItems[currentIdx];

  useEffect(() => {
    (async () => {
      setLoading(true);
      const map = new Map<string, PutawaySuggestion>();
      for (const it of allItems) {
        try {
          const sug = await receivingService.getPutawaySuggestion(warehouseId, it.productId);
          map.set(it.id, sug);
        } catch { /* no suggestion */ }
      }
      setSuggestions(map);
      setLoading(false);
    })();
  }, [warehouseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleScanLocation = useCallback(async (barcode: string) => {
    if (!currentItem) return;
    const suggestion = suggestions.get(currentItem.id);

    if (
      suggestion?.locationCode &&
      (matchesScannedLocation(barcode, suggestion.locationCode) ||
       matchesScannedLocation(barcode, suggestion.suggestedLocationId))
    ) {
      try {
        const res = await receivingService.putaway(sessionId, currentItem.id, suggestion.suggestedLocationId!);
        const backendMsg = res?.message || res?.Message;
        notify({ type: 'success', message: backendMsg || t('screens.receiving.putaway.placedToast', { name: currentItem.productName, location: suggestion.locationCode }) });
        setCompleted(prev => [...prev, currentItem.id]);
        if (currentIdx < allItems.length - 1) {
          setCurrentIdx(prev => prev + 1);
        } else {
          const updated = allItems.map(i => completed.includes(i.id) || i.id === currentItem.id
            ? { ...i, status: 'PutAway', locationCode: suggestions.get(i.id)?.locationCode, locationId: suggestions.get(i.id)?.suggestedLocationId }
            : i
          );
          onDone(updated);
        }
      } catch (err: any) {
        notify({ type: 'error', message: err?.response?.data?.message || err?.response?.data?.Message || t('screens.receiving.putaway.errorPutaway') });
      }
      return;
    }

    // Try finding location by barcode/code from backend
    try {
      // Scanned a different location - try to use it
      const { wmsApi } = await import('../../../../config/apiClient');
      const { ACCOUNTING_API } = await import('../../../../config/api');
      const scanned = normalizeScanValue(barcode);
      let locResp = await wmsApi.get(ACCOUNTING_API.SWL_BY_WAREHOUSE(warehouseId), {
        params: { search: barcode, pageSize: 1 },
      });

      let locations = locResp.data?.data || [];
      if (!Array.isArray(locations)) locations = [];

      // Bazı ortamlarda search parametresi kodu tam eşleştirmeyebilir; fallback tam liste.
      if (locations.length === 0) {
        locResp = await wmsApi.get(ACCOUNTING_API.SWL_BY_WAREHOUSE(warehouseId), {
          params: { page: 1, pageSize: 10000 },
        });
        locations = locResp.data?.data || [];
        if (!Array.isArray(locations)) locations = [];
      }

      const loc = locations.find((x: any) =>
        matchesScannedLocation(scanned, x?.code) ||
        matchesScannedLocation(scanned, x?.Code) ||
        matchesScannedLocation(scanned, x?.barcodeData) ||
        matchesScannedLocation(scanned, x?.BarcodeData) ||
        matchesScannedLocation(scanned, x?.id) ||
        matchesScannedLocation(scanned, x?.Id)
      );

      if (loc) {
        const locId = loc.id || loc.Id;
        const locCode = loc.code || loc.Code;
        if (!locId) {
          notify({ type: 'error', message: t('screens.receiving.putaway.errorLocationIdMissing') });
          return;
        }
        const res = await receivingService.putaway(sessionId, currentItem.id, locId);
        const backendMsg = res?.message || res?.Message;
        notify({ type: 'success', message: backendMsg || t('screens.receiving.putaway.placedToast', { name: currentItem.productName, location: locCode || barcode }) });
        setCompleted(prev => [...prev, currentItem.id]);
        if (currentIdx < allItems.length - 1) {
          setCurrentIdx(prev => prev + 1);
        } else {
          const updated = allItems.map(i => completed.includes(i.id) || i.id === currentItem.id
            ? { ...i, status: 'PutAway', locationCode: locCode, locationId: locId }
            : i
          );
          onDone(updated);
        }
      } else {
        notify({ type: 'error', message: t('screens.receiving.putaway.errorInvalidLocation') });
      }
    } catch (err: any) {
      // Backend artik lokasyon dogrulamasinda anlamli BadRequest mesajlari donuyor
      // (or. "Secilen lokasyon bu depoya ait degil.") — bunlari kullaniciya goster.
      notify({ type: 'error', message: err?.response?.data?.message || err?.response?.data?.Message || t('screens.receiving.putaway.errorLocationValidation') });
    }
  }, [currentItem, suggestions, sessionId, currentIdx, allItems, completed, warehouseId, onDone, notify, t]);

  const handleSkip = useCallback(() => {
    if (currentIdx < allItems.length - 1) {
      setCurrentIdx(prev => prev + 1);
    } else {
      const updated = allItems.map(i => completed.includes(i.id)
        ? { ...i, status: 'PutAway', locationCode: suggestions.get(i.id)?.locationCode }
        : i
      );
      onDone(updated);
    }
  }, [currentIdx, allItems, completed, suggestions, onDone]);

  const handleBulkAcceptAll = useCallback(async () => {
    setLoading(true);
    const itemsToPlace = allItems
      .filter(i => !completed.includes(i.id) && suggestions.get(i.id)?.suggestedLocationId)
      .map(i => ({ itemId: i.id, locationId: suggestions.get(i.id)!.suggestedLocationId! }));

    if (itemsToPlace.length === 0) {
      notify({ type: 'warning', message: t('screens.receiving.putaway.noSuitableLocation') });
      setLoading(false);
      return;
    }

    try {
      const resp = await receivingService.putawayBulk(sessionId, itemsToPlace);
      // Backend kalem bazında sonuç döndürür: [{ ItemId, Success, Message }].
      // SADECE backend'de gerçekten başarılı olanları "PutAway" işaretle; aksi halde
      // DB'de Received kalan kalemi yanlışlıkla yerleştirilmiş gösterip complete'i 400'e düşürürüz.
      const results: any[] = resp?.data ?? resp?.Data ?? resp ?? [];
      const okIds = new Set(
        (Array.isArray(results) ? results : [])
          .filter(r => (r?.success ?? r?.Success) === true)
          .map(r => String(r?.itemId ?? r?.ItemId))
      );
      // Eski/uyumsuz yanıt (sonuç listesi yoksa) → istek throw etmediyse hepsini başarılı say.
      const trustAll = !Array.isArray(results) || results.length === 0;

      const successfulIds = new Set(
        itemsToPlace
          .map(p => p.itemId)
          .filter(id => trustAll || okIds.has(String(id)))
      );
      const failedCount = itemsToPlace.length - successfulIds.size;

      const updated = allItems.map(i => {
        const sug = suggestions.get(i.id);
        if (successfulIds.has(i.id)) {
          return { ...i, status: 'PutAway', locationCode: sug?.locationCode, locationId: sug?.suggestedLocationId };
        }
        return completed.includes(i.id) ? { ...i, status: 'PutAway' } : i;
      });

      if (failedCount > 0) {
        // Backend'in reddettigi ilk kalemin gerekcesini de goster (varsa).
        const firstFailMsg = (Array.isArray(results) ? results : [])
          .filter(r => (r?.success ?? r?.Success) === false)
          .map(r => r?.message ?? r?.Message)
          .find((m: any) => !!m);
        const baseMsg = t('screens.receiving.putaway.bulkPartial', { success: successfulIds.size, failed: failedCount });
        notify({ type: 'warning', message: firstFailMsg ? `${baseMsg} — ${firstFailMsg}` : baseMsg });
      } else {
        notify({ type: 'success', message: t('screens.receiving.putaway.bulkSuccess', { count: successfulIds.size }) });
      }
      onDone(updated);
    } catch (err: any) {
      notify({ type: 'error', message: err?.response?.data?.message || err?.response?.data?.Message || t('screens.receiving.putaway.errorBulkPutaway') });
    }
    setLoading(false);
  }, [allItems, completed, suggestions, sessionId, onDone, notify, t]);

  // Harita renkleri: tamamlanan öneri lokasyonları yeşil, bekleyen diğer öneriler gri.
  const { placedLocationIds, pendingLocationIds } = useMemo(() => {
    const placed = new Set<string>();
    const pending = new Set<string>();
    allItems.forEach((it, idx) => {
      const locId = suggestions.get(it.id)?.suggestedLocationId;
      if (!locId) return;
      if (idx === currentIdx) return; // aktif kalem hedef olarak ayrı renklenir
      if (completed.includes(it.id)) placed.add(locId);
      else pending.add(locId);
    });
    return { placedLocationIds: placed, pendingLocationIds: pending };
  }, [allItems, suggestions, completed, currentIdx]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  const suggestion = currentItem ? suggestions.get(currentItem.id) : undefined;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <WmsNotification notification={notification} onClose={clear} />

      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        {isBulk
          ? t('screens.receiving.putaway.titleWithProgress', { current: currentIdx + 1, total: allItems.length })
          : t('screens.receiving.putaway.title')}
      </Typography>

      {currentItem && (
        <>
          <Paper sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{currentItem.productName}</Typography>
            <Typography variant="body1">{t('screens.receiving.putaway.quantityValue', { count: currentItem.receivedQuantity })}</Typography>
            {currentItem.barcode && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>{t('screens.receiving.putaway.barcodeLabel', { barcode: currentItem.barcode })}</Typography>
            )}
          </Paper>

          {suggestion && suggestion.suggestedLocationId && (
            <Paper sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'success.main' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PlaceIcon color="success" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'success.main' }}>
                  {t('screens.receiving.putaway.suggestedTitle')}
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                {suggestion.locationCode}
              </Typography>
              {suggestion.locationName && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>{suggestion.locationName}</Typography>
              )}
              <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
                <Typography variant="body2">
                  {t('screens.receiving.putaway.currentStock', { count: suggestion.currentStock })}
                </Typography>
                {suggestion.capacity != null && (
                  <Typography variant="body2">
                    {t('screens.receiving.putaway.capacity', { count: suggestion.capacity })}
                  </Typography>
                )}
              </Box>
              <Chip label={suggestion.reason} size="small" color="info" variant="outlined" sx={{ mt: 1 }} />
            </Paper>
          )}

          {suggestion && !suggestion.suggestedLocationId && (
            <Paper sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'warning.main' }}>
              <Typography color="warning.main" sx={{ fontWeight: 600 }}>
                {suggestion.reason}
              </Typography>
            </Paper>
          )}

          {suggestion?.suggestedLocationId && (
            <ReceivingNavMap
              warehouseId={warehouseId}
              targetLocationId={suggestion.suggestedLocationId}
              targetLocationCode={suggestion.locationCode}
              placedLocationIds={placedLocationIds}
              pendingLocationIds={pendingLocationIds}
              subtitle={currentItem.productName}
              height={260}
            />
          )}

          <Paper sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'center' }}>
              {t('screens.receiving.putaway.scanLocationTitle')}
            </Typography>
            <Box sx={{ maxWidth: 500, mx: 'auto' }}>
              <WmsBarcodeScanner onScan={handleScanLocation} placeholder={t('screens.receiving.putaway.scanLocationPlaceholder')} />
            </Box>
          </Paper>
        </>
      )}

      {isBulk && (
        <Paper sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, flex: 1, overflow: 'auto' }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('screens.receiving.putaway.putawayListTitle')}</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>{t('common.product')}</TableCell>
                <TableCell align="center">{t('screens.receiving.putaway.colQty')}</TableCell>
                <TableCell>{t('screens.receiving.putaway.colSuggestedLocation')}</TableCell>
                <TableCell align="center">{t('common.status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allItems.map((it, idx) => {
                const sug = suggestions.get(it.id);
                const done = completed.includes(it.id);
                return (
                  <TableRow key={it.id} sx={{
                    bgcolor: idx === currentIdx ? 'action.selected' : undefined,
                    opacity: done ? 0.5 : 1,
                  }}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{it.productName || '-'}</TableCell>
                    <TableCell align="center">{it.receivedQuantity}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {sug?.locationCode || '-'}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={done ? t('screens.receiving.putaway.statusPutAway') : idx === currentIdx ? t('screens.receiving.putaway.statusActive') : t('screens.receiving.putaway.statusWaiting')}
                        size="small"
                        color={done ? 'success' : idx === currentIdx ? 'warning' : 'default'}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={onBack} size="large">
          {t('screens.receiving.putaway.back')}
        </Button>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<SkipNextIcon />} onClick={handleSkip} size="large">
            {t('screens.receiving.putaway.skip')}
          </Button>
          {isBulk && (
            <Button variant="contained" startIcon={<CheckIcon />} onClick={handleBulkAcceptAll}
              size="large" color="success" sx={{ fontWeight: 600 }}>
              {t('screens.receiving.putaway.acceptAll')}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ReceivingPutaway;
